import * as React from 'react'
import {exhaustive} from '@freckle/exhaustive'
import {fromJust} from '@freckle/maybe'

import {type DemoDistrictT} from '@frontrow/console/src/models/demo-district'
import NotRequestedPanel from '@frontrow/console/src/components/pages/demo-districts-page/status-panels/not-requested'
import RequestedPanel from '@frontrow/console/src/components/pages/demo-districts-page/status-panels/requested'
import CreatedPanel from '@frontrow/console/src/components/pages/demo-districts-page/status-panels/created'
import CreationFailedPanel from '@frontrow/console/src/components/pages/demo-districts-page/status-panels/creation-failed'

type StatusT = 'not-requested' | 'requested' | 'creating' | 'created' | 'creation-failed'

type Props = {
  demoDistrictData: DemoDistrictT
  createOrResetDemoDistrict: () => void
}

export default function DemoDistrictPage(props: Props): React.ReactElement {
  const {demoDistrictData, createOrResetDemoDistrict} = props
  const {requestedAt, createdAt, creationFailedReason, owner, loginPassword} = demoDistrictData
  const status = getStatus(demoDistrictData)

  switch (status) {
    case 'not-requested':
      return <NotRequestedPanel createDemoDistrict={createOrResetDemoDistrict} />
    case 'requested':
    case 'creating':
      const requestedAtNotMaybe = fromJust(requestedAt, `exception`)
      return <RequestedPanel requestedAt={requestedAtNotMaybe} />
    case 'created':
      const createdAtNotMaybe = fromJust(createdAt, `exception`)
      const loginPasswordNotMaybe = fromJust(loginPassword, `exception`)
      return (
        <CreatedPanel
          owner={owner}
          createdAt={createdAtNotMaybe}
          loginPassword={loginPasswordNotMaybe}
          resetDemoDistrict={createOrResetDemoDistrict}
        />
      )
    case 'creation-failed':
      const creationFailedReasonNotMaybe = fromJust(creationFailedReason, `exception`)
      return (
        <CreationFailedPanel
          creationFailedReason={creationFailedReasonNotMaybe}
          resetDemoDistrict={createOrResetDemoDistrict}
        />
      )
    default:
      return exhaustive(status, `Invalid demo district status ${status}`)
  }
}

function getStatus(demoDistrictData: DemoDistrictT): StatusT {
  const {requestedAt, creationStartedAt, createdAt, creationFailedReason} = demoDistrictData

  if (creationFailedReason !== null && creationFailedReason !== undefined) {
    return 'creation-failed'
  }

  if (createdAt !== null && createdAt !== undefined) {
    return 'created'
  }

  if (creationStartedAt !== null && creationStartedAt !== undefined) {
    return 'creating'
  }

  if (requestedAt !== null && requestedAt !== undefined) {
    return 'requested'
  }

  return 'not-requested'
}
